import { useTranslation } from "react-i18next";
import defaultContent from "../../../content/ssiUplineReport";
import LeafPanel from "../../../common/leaf/LeafPanel/LeafPanel";
import ProfileLoader from "../../../components/ABOProfile/ProfileLoader/ProfileLoader";
import SSIUplineAboDetails from "./SSIUplineAboDetails";
import { ProfileTransformResponse } from "../../../services/Profile/profileAPI.types";
import { LeafErrorMessage } from "../../../common/leaf/LeafErrorMessage/LeafErrorMessage";
import { SSIEligibility } from "../../../services/StrongStart/ssiAPI.types";
import SSIEligibilityDetails from "../../StrongStart/SSIEligibilityModal/SSIEligibilityDetails";
import "./SSIUplineProfile.scss";
import { useAppSelector } from "../../../store";
import { ssiUplineReportTabs } from "../../../reducers/ssiUplineReport";
import { BADGE_TYPE } from "../../../common/icons/IconShield/IconShield";
import { CA_MARKET_CODES } from "../../../common/constants/market-constants";

type Props = {
  aboNo: string;
  affNo: string;
  sponsorNo: string;
  sponsorAff: string;
  eligibilityWindow: SSIEligibility;
  showEligibilityWindow: boolean;
  show: boolean;
  aboDataFetching: boolean;
  sponsorDataFetching: boolean;
  aboData: Partial<ProfileTransformResponse> | undefined;
  sponsorData: Partial<ProfileTransformResponse> | undefined;
  isError: boolean;
  onCloseClick: Function;
};

const SSIUplineProfileView = (props: Props) => {
  const {
    aboNo,
    affNo,
    sponsorNo,
    sponsorAff,
    eligibilityWindow,
    showEligibilityWindow,
    show,
    aboDataFetching,
    sponsorDataFetching,
    aboData,
    sponsorData,
    isError,
    onCloseClick,
  } = props;
  const { t } = useTranslation(["ssiUplineReport"]);
  const activeView = useAppSelector((state) => state.ssiUplineReportTracker.activeView);

  const user = useAppSelector((state) => state.boot?.user);
  const isCAMarket = CA_MARKET_CODES.includes(user.aff);

  const hasStartPeriod = !!eligibilityWindow?.StartPeriod;
  let legendBadges: BADGE_TYPE[] = [];

  const ORDERED_BADGES = [
    BADGE_TYPE.EARNED,
    BADGE_TYPE.POTENTIAL,
    BADGE_TYPE.NOT_EARNED,
    BADGE_TYPE.PAYDAY_ACHIEVED,
    BADGE_TYPE.PAYDAY_POTENTIAL,
  ];

  // Helper to filter in correct sequence
  const arrangeBadges = (list: BADGE_TYPE[]) => ORDERED_BADGES.filter((b) => list.includes(b));

  switch (activeView) {
    case ssiUplineReportTabs.IN_SSI_PROGRAM_TAB: {
      const baseBadges = [BADGE_TYPE.EARNED, BADGE_TYPE.POTENTIAL];

      const withPayday = hasStartPeriod
        ? [...baseBadges, BADGE_TYPE.PAYDAY_ACHIEVED, BADGE_TYPE.PAYDAY_POTENTIAL]
        : baseBadges;

      const withNotEarned = isCAMarket ? [...withPayday, BADGE_TYPE.NOT_EARNED] : withPayday;

      legendBadges = arrangeBadges(withNotEarned);
      break;
    }

    case ssiUplineReportTabs.EARNED_PAYMENT_TAB: {
      const baseBadges = [BADGE_TYPE.EARNED, BADGE_TYPE.POTENTIAL];

      const withPayday = hasStartPeriod
        ? [...baseBadges, BADGE_TYPE.PAYDAY_ACHIEVED, BADGE_TYPE.PAYDAY_POTENTIAL]
        : baseBadges;

      const withNotEarned = isCAMarket ? [...withPayday, BADGE_TYPE.NOT_EARNED] : withPayday;

      legendBadges = arrangeBadges(withNotEarned);
      break;
    }

    case ssiUplineReportTabs.OUT_SSI_PROGRAM_TAB: {
      legendBadges = [BADGE_TYPE.EARNED, BADGE_TYPE.NOT_EARNED, BADGE_TYPE.UNAVAILABLE, BADGE_TYPE.PAYDAY_ACHIEVED];
      break;
    }

    default:
      legendBadges = [];
  }

  const renderAboDetails = () => {
    if (aboData?.aboInfo) {
      const { name, localName, privacyFlag, isoLanguageCode } = aboData.aboInfo;
      if (aboData?.partyInfo) {
        const primaryParty = aboData.partyInfo.find(({ isPrimary }) => isPrimary === true) || aboData.partyInfo[0];
        const { phoneNumbers, emailAddresses } = primaryParty;
        const { displayNumber } = phoneNumbers[0] || {};
        const { email } = emailAddresses[0] || {};

        return (
          <SSIUplineAboDetails
            abo={{
              affNo: affNo,
              aboNo: aboNo,
              name: name,
              localName: localName,
              isoLanguageCode: isoLanguageCode,
              privacyFlag: privacyFlag,
              primaryNumber: displayNumber,
              email: email,
            }}
            type={"abo"}
          />
        );
      }
    }

    if (isError) {
      // TODO :: tealium tagging track event for error
    }

    return (
      <div className="SSIUplineProfile__Section">
        <LeafErrorMessage show={isError} />
      </div>
    );
  };

  const renderSponsorDetails = () => {
    if (sponsorData?.aboInfo) {
      const { aboInfo, partyInfo } = sponsorData;
      const { name, localName, privacyFlag, isoLanguageCode } = aboInfo;

      const primaryParty = partyInfo?.find(({ isPrimary }) => isPrimary === true) ?? partyInfo?.[0];
      const primaryNumber = primaryParty?.phoneNumbers?.[0]?.displayNumber || "";
      const email = primaryParty?.emailAddresses?.[0]?.email || "";

      return (
        <SSIUplineAboDetails
          abo={{
            affNo: sponsorAff,
            aboNo: sponsorNo,
            name: name,
            localName: localName,
            isoLanguageCode: isoLanguageCode,
            privacyFlag: privacyFlag,
            primaryNumber: primaryNumber,
            email: email,
          }}
          type={"sponsor"}
        />
      );
    }

    return <></>;
  };

  return (
    <LeafPanel
      title={t("profileModal.title", defaultContent["profileModal"]["title"]).toString()}
      show={show}
      onCloseClick={onCloseClick}
      id={"ssi-upline-abo-profile"}
      applyScroll={true}
    >
      <div className="SSIUplineProfile">
        {aboDataFetching ? (
          <div className="SSIUplineProfile__Section">
            <ProfileLoader row={3} isFetching={aboDataFetching} />
          </div>
        ) : (
          renderAboDetails()
        )}
        {sponsorDataFetching ? (
          <div className="SSIUplineProfile__Section">
            <ProfileLoader row={3} isFetching={sponsorDataFetching} />
          </div>
        ) : (
          renderSponsorDetails()
        )}
        {showEligibilityWindow && (
          <div className="SSI__eligibility-modal">
            <SSIEligibilityDetails isLoading={false} eligibilityData={eligibilityWindow} legendBadges={legendBadges} />
          </div>
        )}
      </div>
    </LeafPanel>
  );
};

export default SSIUplineProfileView;
